import { useState, useMemo, useCallback } from "react"
import { useMetricDictionary } from "~/ui-rtk/hooks/metric-dictionary"
import type {
  MetricCategory,
  MetricPlatform,
  ChannelType,
  BusinessArea,
} from "~/ui-rtk/constants/metric-dictionary"

export type Filters = {
  category?: MetricCategory
  platform?: MetricPlatform
  channelType?: ChannelType
  businessArea?: BusinessArea
  search?: string
}

export default function useConnect() {
  const [filters, setFilters] = useState<Filters>({})

  const { metrics, totalCount, isLoading, error, refetch } =
    useMetricDictionary(filters)

  const handleFilterChange = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K] | undefined) => {
      setFilters(prev => ({
        ...prev,
        [key]: value || undefined,
      }))
    },
    [],
  )

  const handleSearchChange = useCallback((search: string) => {
    setFilters(prev => ({
      ...prev,
      search: search || undefined,
    }))
  }, [])

  const handleClearFilters = useCallback(() => {
    setFilters({})
  }, [])

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(v => v !== undefined),
    [filters],
  )

  return {
    metrics,
    totalCount,
    isLoading,
    error,
    filters,
    hasActiveFilters,
    handleFilterChange,
    handleSearchChange,
    handleClearFilters,
    refetch,
  }
}
